import React, { useState, useEffect } from "react";
import {
  updateGalleryData,
  getGalleryData,
  deleteGalleryData,
  getAllDataWithFlag,
  addGalleryData
} from "../../../../services/CommonService";
import { imgURL, aboutus_WIDTH, aboutus_HEIGHT } from "../../../Config";
import Swal from "sweetalert2";
import "./GalleryManager.css";
import { exportToExcel } from '../../../../Utils/common-Excel';
import PaginationControls from "../../../../Utils/PaginationControls";
import usePagination from "../../../../Utils/usePagination";
import Project from "../Project/Project";

const GalleryManager = () => {
  const [galleryItems, setGalleryItems] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [currentItem, setCurrentItem] = useState({
    id: null,
    title: "",
    description: "",
    year: new Date().getFullYear().toString(),
    type: "",
    link: '',
    media_path: "",
    thumbnail: "",
    project_id: ''
  });
  const [isEditing, setIsEditing] = useState(false);

  const [selectedMediaFile, setSelectedMediaFile] = useState(null);
  const [selectedThumbnailFile, setSelectedThumbnailFile] = useState(null);

  const [mediaPreviewUrl, setMediaPreviewUrl] = useState("");
  const [thumbnailPreviewUrl, setThumbnailPreviewUrl] = useState("");
  const [typeofContent, setTypeofContent] = useState("");
  const [typeofMode, setTypeofMode] = useState("");
  const [projectData, setProjectData] = useState([]);
  const [itemsPerPage, setItemsPerPage] = useState(5);

  // Add search state
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredItems, setFilteredItems] = useState([]);

  // Form validation state
  const [errors, setErrors] = useState({
    title: "",
    description: "",
    year: "",
    media_path: "",
    thumbnail: ""
  });
  const [touched, setTouched] = useState({
    title: false,
    description: false,
    year: false,
    media_path: false,
    thumbnail: false,
    link: false,
    type: false
  });

  const MAX_FILE_SIZE = 1 * 1024 * 1024; // 1MB in bytes
  const REQUIRED_DIMENSIONS = {
    media: { width: 800, height: 600 }, // Adjust as needed
    thumbnail: { width: 322, height: 318 } // Adjust as needed
  };

  // Fetch gallery items on mount
  useEffect(() => {
    const lookupData = JSON.parse(localStorage.getItem('lookup'));
    setTypeofContent(lookupData.filter((m) => m.Flag == '4'));
    setTypeofMode(lookupData.filter((m) => m.Flag == '7'))

    const fetchGalleryItems = async () => {
      try {
        const response = await getGalleryData();
        setGalleryItems(response.data);
      } catch (error) {
        Swal.fire({
          title: "Error",
          text: error?.response?.data?.message || error?.message,
          icon: "error",
          confirmButtonText: "OK",
        });
      }
    };
    fetchGalleryItems();
    getProjectData();
  }, []);
  const getProject = (id) => {
    return projectData.find((p) => p.id === id);
  };
  const getTypeMode = (code) => {
    const match = typeofMode.find(t => String(t.LookupCode) === String(code));
    return match ? match.LookupValue : code; // fallback to code if not found
  };
  const getContentTypeName = (code) => {
    const match = typeofContent.find(t => String(t.LookupCode) === String(code));
    return match ? match.LookupValue : code; // fallback to code if not found
  };
  // excel download
  const handleDownloadExcel = () => {
    const formatDate = (isoString) => {
      const date = new Date(isoString);
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0'); // Months are 0-based
      const year = date.getFullYear();
      return `${day}-${month}-${year}`;
    };
    const exportData = filteredItems?.map((item, index) => {
      const project = getProject(item.project_id);
      return {
        'S.No.': index + 1,
        Title: item.title,
        Year: item.year,
        Type: getContentTypeName(item.type),
        Mode: getTypeMode(item.link),
        "Project": project?.title || item.project_id,
        Description: item.description,
        Media: item.media_path,
        Thumbnail: item.thumbnail,
        'Created on': formatDate(item.created_at),
        'Updated on': formatDate(item.updated_at)
      }
    });

    exportToExcel(exportData, 'GalleryList', 'Gallery', 'Gallery Report', null, null);
  };



  // Clean up Blob URLs
  useEffect(() => {
    return () => {
      if (mediaPreviewUrl && mediaPreviewUrl.startsWith("blob:")) {
        URL.revokeObjectURL(mediaPreviewUrl);
      }
      if (thumbnailPreviewUrl && thumbnailPreviewUrl.startsWith("blob:")) {
        URL.revokeObjectURL(thumbnailPreviewUrl);
      }
    };
  }, [mediaPreviewUrl, thumbnailPreviewUrl]);

  useEffect(() => {
    setFilteredItems(galleryItems);
  }, [galleryItems]);

  const getProjectData = async () => {
    try {
      const response = await getAllDataWithFlag(5);
      setProjectData(response?.data?.project)
    } catch (err) {
      Swal.fire({
        title: "Error",
        text: err?.response?.data?.message || err?.message,
        icon: "error",
        confirmButtonText: "OK",
      });
    }
  }

  // Handle search input changes
  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1); // Reset to first page when searching
  };

  const filtered = galleryItems.filter(
    (item) =>
      item.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.year.toString().includes(searchTerm)
  );

  const { currentPage, setCurrentPage, totalPages, paginatedData: filteredData } = usePagination(filtered, itemsPerPage);

  // Validate form fields
  const validateField = (name, value) => {
    let errorMessage = "";

    switch (name) {
      case "title":
        if (!value?.trim()) {
          errorMessage = "Title is required";
        } else if (value.trim().length > 100) {
          errorMessage = "Title must be less than 100 characters";
        }
        break;
      case "description":
        if (!value?.trim()) {
          errorMessage = "Description is required";
        }
        break;
      case "link":
        if (!value) {
          errorMessage = "Mode is required";
        }
        break;
      case "type":
        if (!value) {
          errorMessage = "Type is required";
        }
        break;
      case "year":
        if (!value) {
          errorMessage = "Year is required";
        }
        break;
      case "media_path":
        if (currentItem.type == 2 && !value?.trim()) {
          errorMessage = "Video URL is required";
        } else if (currentItem.type == 2 && !isValidURL(value)) {
          errorMessage = "Please enter a valid URL";
        }
        break;
      default:
        break;
    }

    return errorMessage;
  };

  // Helper function to validate URLs
  const isValidURL = (url) => {
    try {
      new URL(url);
      return true;
    } catch (error) {
      return false;
    }
  };

  // Validate all form fields
  const validateForm = () => {
    const newErrors = {};
    let isValid = true;

    // Validate text fields
    Object.keys(currentItem).forEach(key => {
      if (["title", "description", "year", "link", "type"].includes(key)) {
        const error = validateField(key, currentItem[key]);
        newErrors[key] = error;
        if (error) isValid = false;
      }
    });

    // Validate media based on type
    if (currentItem.type == 2) { // Video type
      const mediaError = validateField("media_path", currentItem.media_path);
      newErrors.media_path = mediaError;
      if (mediaError) isValid = false;
    } else if (currentItem.type == 1 && currentItem.link == 1 && !isEditing && !selectedMediaFile) { // Photo type and not editing
      newErrors.media_path = "Media image is required";
      isValid = false;
    } else if (currentItem.type == 1 && currentItem.link == 2 && !isEditing && !selectedMediaFile && currentItem.media_path && !isValidImageURL(currentItem.media_path)) {
      newErrors.media_path = "Invalid url.";
      isValid = false;
    } else if (currentItem.type == 1 && currentItem.link == 2 && !isEditing && !selectedMediaFile) {
      newErrors.media_path = "URL is required.";
      isValid = false;
    }

    // Validate thumbnail (required for new items)
    if (!isEditing && !selectedThumbnailFile) {
      newErrors.thumbnail = "Thumbnail image is required";
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name == "type" && value == 2) currentItem.link = 2;
    // Update current item
    setCurrentItem({ ...currentItem, [name]: value });

    // Mark field as touched
    setTouched({ ...touched, [name]: true });

    // Validate field
    const errorMessage = validateField(name, value);
    setErrors({ ...errors, [name]: errorMessage });
  };

  const handleBlur = (e) => {
    const { name } = e.target;
    setTouched({ ...touched, [name]: true });

    const errorMessage = validateField(name, currentItem[name]);
    setErrors({ ...errors, [name]: errorMessage });
  };

  const handleFileChange = async (e) => {
    const { name, files } = e.target;
    const file = files && files[0];

    if (
      name === "mediaFileInput" &&
      mediaPreviewUrl &&
      mediaPreviewUrl.startsWith("blob:")
    ) {
      URL.revokeObjectURL(mediaPreviewUrl);
    } else if (
      name === "thumbnailFileInput" &&
      thumbnailPreviewUrl &&
      thumbnailPreviewUrl.startsWith("blob:")
    ) {
      URL.revokeObjectURL(thumbnailPreviewUrl);
    }

    if (file) {
      // Validate image file
      const validationType = name === "mediaFileInput" ? "media" : "thumbnail";
      const validation = await validateImageFile(file, validationType);
      if (!validation.isValid) {
        // Show validation error
        const errorField = name === "mediaFileInput" ? "media_path" : "thumbnail";
        setTouched({ ...touched, [errorField]: true });
        setErrors({ ...errors, [errorField]: validation.error });
        // Clear the file input
        e.target.value = '';
        return;
      }

      const previewUrl = URL.createObjectURL(file);
      if (name === "mediaFileInput") {
        setSelectedMediaFile(file);
        setMediaPreviewUrl(previewUrl);
        setErrors({ ...errors, media_path: "" });
      } else if (name === "thumbnailFileInput") {
        setSelectedThumbnailFile(file);
        setThumbnailPreviewUrl(previewUrl);
        setErrors({ ...errors, thumbnail: "" });
      }
    } else {
      if (name === "mediaFileInput") {
        setSelectedMediaFile(null);
        setMediaPreviewUrl(
          isEditing && currentItem.media_path
            ? imgURL + currentItem.media_path
            : ""
        );
      } else if (name === "thumbnailFileInput") {
        setSelectedThumbnailFile(null);
        setThumbnailPreviewUrl(
          isEditing && currentItem.thumbnail
            ? imgURL + currentItem.thumbnail
            : ""
        );
      }
    }
  };

  const openForm = (item = null) => {
    // Clean up blob URLs from potential previous form instance
    if (mediaPreviewUrl && mediaPreviewUrl.startsWith("blob:")) {
      URL.revokeObjectURL(mediaPreviewUrl);
    }
    if (thumbnailPreviewUrl && thumbnailPreviewUrl.startsWith("blob:")) {
      URL.revokeObjectURL(thumbnailPreviewUrl);
    }

    // Reset validation state
    setErrors({
      title: "",
      description: "",
      year: "",
      media_path: "",
      thumbnail: ""
    });

    setTouched({
      title: false,
      description: false,
      year: false,
      media_path: false,
      thumbnail: false,
      link: false,
      type: false
    });

    if (item) {
      setCurrentItem({
        ...item,
        media_path: item.media_path || "",
        thumbnail: item.thumbnail || "",
      });
      setIsEditing(true);

      setMediaPreviewUrl(item.media_path ? imgURL + item.media_path : "");
      setThumbnailPreviewUrl(item.thumbnail ? imgURL + item.thumbnail : "");

      setSelectedMediaFile(null);
      setSelectedThumbnailFile(null);
    } else {
      setCurrentItem({
        id: null,
        title: "",
        description: "",
        year: new Date().getFullYear().toString(),
        type: "",
        link: "",
        media_path: "",
        thumbnail: "",
      });
      setIsEditing(false);

      setSelectedMediaFile(null);
      setSelectedThumbnailFile(null);
      setMediaPreviewUrl("");
      setThumbnailPreviewUrl("");
    }
    setShowForm(true);
  };

  const cancelForm = () => {
    // Clean up blob URLs on form close
    if (mediaPreviewUrl && mediaPreviewUrl.startsWith("blob:")) {
      URL.revokeObjectURL(mediaPreviewUrl);
    }
    if (thumbnailPreviewUrl && thumbnailPreviewUrl.startsWith("blob:")) {
      URL.revokeObjectURL(thumbnailPreviewUrl);
    }

    setShowForm(false);
    setCurrentItem({
      id: null,
      title: "",
      description: "",
      year: new Date().getFullYear().toString(),
      type: "",
      link: "",
      media_path: "",
      thumbnail: "",
    });
    setSelectedMediaFile(null);
    setSelectedThumbnailFile(null);
    setMediaPreviewUrl("");
    setThumbnailPreviewUrl("");

    // Reset validation state
    setErrors({
      title: "",
      description: "",
      year: "",
      media_path: "",
      thumbnail: ""
    });

    setTouched({
      title: false,
      description: false,
      year: false,
      media_path: false,
      thumbnail: false
    });
  };

  const isValidImageURL = (url) => {
    if (!isValidURL(url)) return false;
    return /\.(jpeg|jpg|gif|png|webp|bmp|svg)$/i.test(url);
  };

  // Submit data
  const handleSubmit = async () => {
    try {
      // Mark all fields as touched to show validation errors
      setTouched({
        title: true,
        description: true,
        year: true,
        media_path: true,
        thumbnail: true,
        link: true,
        type: true
      });

      // Validate the form
      const isValid = validateForm();

      if (!isValid) {
        Swal.fire({
          title: "Validation Error",
          icon: "error",
          text: "Please fix the validation errors before submitting.",
          confirmButtonText: "Ok",
        });
        return;
      }

      const formData = new FormData();
      if (isEditing && currentItem.id) {
        formData.append("id", currentItem.id);
      }
      formData.append("title", currentItem.title);
      formData.append("description", currentItem.description);
      formData.append("year", currentItem.year);
      formData.append("type", currentItem.type);
      formData.append("link", currentItem.link);
      formData.append("project_id", (currentItem.project_id || ''));

      if (isEditing) {
        if (selectedMediaFile) {
          formData.append("media", selectedMediaFile);
        } else if (isValidURL(currentItem.media_path)) {
          formData.append("media", currentItem.media_path);
        }
        if (selectedThumbnailFile) {
          formData.append("thumbnail", selectedThumbnailFile);
        }
        const response = await updateGalleryData(formData, currentItem.id);
      } else {
        if (selectedMediaFile) {
          formData.append("media", selectedMediaFile);
        } else if (currentItem.media_path) {
          formData.append("media", currentItem.media_path);
        }
        if (selectedThumbnailFile) {
          formData.append("thumbnail", selectedThumbnailFile);
        } else if (currentItem.thumbnail) {
          formData.append("thumbnail", currentItem.thumbnail);
        }
        const response = await addGalleryData(formData);
      }

      const refreshResponse = await getGalleryData();
      setGalleryItems(refreshResponse.data);
      cancelForm();
      Swal.fire({
        title: "Success!",
        icon: "success",
        text: `Gallery item ${isEditing ? "updated" : "added"} successfully!`,
        confirmButtonText: "ok",
      });
    } catch (error) {
      Swal.fire({
        title: "Error",
        text: error?.response?.data?.message || error?.message,
        icon: "error",
        confirmButtonText: "OK",
      });
    }
  };

  //delete
  const handleDelete = async (id) => {
    Swal.fire({
      title: "Are you sure you want to delete this item?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "No, cancel!",
      reverseButtons: true,
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await deleteGalleryData(id);
          const updatedItems = galleryItems.filter((item) => item.id !== id);
          setGalleryItems(updatedItems);
          Swal.fire({
            title: "Success!",
            icon: "success",
            text: `Gallery item deleted successfully!`,
            confirmButtonText: "ok",
          });
        } catch (error) {
          Swal.fire({
            title: "Error",
            text: error?.response?.data?.message || error?.message,
            icon: "error",
            confirmButtonText: "OK",
          });
        }
      }
    });
  };

  const validateImageFile = (file, type = 'media') => {
    return new Promise((resolve) => {
      // Check file size
      if (file.size > MAX_FILE_SIZE) {
        resolve({
          isValid: false,
          error: `File size must be less than ${MAX_FILE_SIZE / (1024 * 1024)} MB`
        });
        return;
      }

      // Check image dimensions
      const img = new Image();
      const objectUrl = URL.createObjectURL(file);

      img.onload = function () {
        URL.revokeObjectURL(objectUrl);
        const requiredDim = REQUIRED_DIMENSIONS[type];

        if (this.width < requiredDim.width || this.height < requiredDim.height) {
          resolve({
            isValid: false,
            error: `Image dimensions must be ${requiredDim.width} x ${requiredDim.height} px. Current: ${this.width} x ${this.height}`
          });
        } else {
          resolve({ isValid: true, error: null });
        }
      };

      img.onerror = function () {
        URL.revokeObjectURL(objectUrl);
        resolve({ isValid: false, error: 'Invalid image file' });
      };

      img.src = objectUrl;
    });
  };

  return (
    <div className="container-fluid gallery py-4">
      {showForm ? (
        <div className="row">
          <div className="col-md-12">
            <div className="card">
              <div className="card-header adminCardHeader d-flex justify-content-between align-items-center">
                <h4>{isEditing ? "Edit Gallery Item" : "Add New Gallery Item"}</h4>
                <button
                  onClick={cancelForm}
                  className="btn btn-sm btn-warning"
                >
                  <i className="bi bi-arrow-left me-1"></i> Back
                </button>
              </div>
              <div className="card-body">
                <form className="row g-3" onSubmit={(e) => e.preventDefault()}>
                  <div className="col-md-4">
                    <label htmlFor="title" className="form-label">
                      Title <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      name="title"
                      id="title"
                      value={currentItem.title}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      className={`form-control ${touched.title && errors.title ? "is-invalid" : ""}`}
                    />
                    {touched.title && errors.title && (
                      <div className="invalid-feedback">{errors.title}</div>
                    )}
                  </div>
                  <div className="col-md-4">
                    <label htmlFor="year" className="form-label">
                      Year
                    </label>
                    <select
                      name="year"
                      id="year"
                      value={currentItem.year}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      className={`form-select`}
                    >
                      <option value=''>Select Year</option>
                      {Array.from({ length: 25 }, (_, i) => {
                        const year = new Date().getFullYear() - i;
                        return (
                          <option key={year} value={year}>
                            {year}
                          </option>
                        );
                      })}
                    </select>
                  </div>
                  <div className="col-md-4">
                    <label htmlFor="type" className="form-label">
                      Type <span className="text-danger">*</span>
                    </label>
                    <select
                      name="type"
                      id="type"
                      value={currentItem.type}
                      onChange={handleInputChange}
                      className={`form-select ${touched.type && errors.type ? "is-invalid" : ""}`}
                    >
                      <option value=''>Select Type</option>
                      {typeofContent &&
                        typeofContent.map((m) => (
                          <option key={m.LookupCode} value={m.LookupCode}>
                            {m.LookupValue}
                          </option>
                        ))
                      }
                    </select>
                    {touched.type && errors.type && (
                      <div className="invalid-feedback">{errors.type}</div>
                    )}
                  </div>
                  <div className="col-md-4">
                    <label htmlFor="link" className="form-label">
                      Mode <span className="text-danger">*</span>
                    </label>
                    <select
                      name="link"
                      id="link"
                      value={currentItem.link}
                      onChange={handleInputChange}
                      className={`form-select ${touched.link && errors.link ? "is-invalid" : ""}`}
                      disabled={currentItem.type == 2}
                    >
                      <option value=''>Select Mode</option>
                      {typeofMode &&
                        typeofMode.map((m) => (
                          <option key={m.LookupCode} value={m.LookupCode}>
                            {m.LookupValue}
                          </option>
                        ))
                      }
                    </select>
                    {touched.link && errors.link && (
                      <div className="invalid-feedback">{errors.link}</div>
                    )}
                  </div>
                  <div className="col-md-4">
                    <label htmlFor="project_id" className="form-label">
                      Project
                    </label>
                    <select
                      name="project_id"
                      id="project_id"
                      value={currentItem.project_id ?? ''}
                      onChange={handleInputChange}
                      className="form-select"
                    >
                      <option value=''>Select Project</option>
                      {projectData?.length &&
                        projectData.map((m, i) => (
                          <option key={i} value={m.id}>
                            {m.title}
                          </option>
                        ))
                      }
                    </select>
                  </div>
                  <div className="col-12">
                    <label htmlFor="description" className="form-label">
                      Description <span className="text-danger">*</span>
                    </label>
                    <textarea
                      name="description"
                      id="description"
                      value={currentItem.description}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      rows="4"
                      className={`form-control ${touched.description && errors.description ? "is-invalid" : ""}`}
                    ></textarea>
                    {touched.description && errors.description && (
                      <div className="invalid-feedback">{errors.description}</div>
                    )}
                  </div>
                  <div className="col-md-6">
                    <label
                      htmlFor={
                        (currentItem.type == 2 || currentItem.link == 2)
                          ? "media-url"
                          : "media-upload"
                      }
                      className="form-label"
                    >
                      {currentItem.type == 2
                        ? "Video Link (URL)"
                        : currentItem.link == 1 ? `Media Image File (${REQUIRED_DIMENSIONS.media.width} x ${REQUIRED_DIMENSIONS.media.height} px, Max size: ${MAX_FILE_SIZE / (1024 * 1024)} MB)` : `Image Link (URL)`}
                      {!isEditing &&
                        (currentItem.type == 1 || currentItem.type == 2) && (
                          <span className="text-danger ms-1">*</span>
                        )
                      }
                    </label>
                    {(currentItem.type == 2 || currentItem.link == 2) ? (
                      <div>
                        <input
                          type="text"
                          name="media_path"
                          id="media-url"
                          value={currentItem.media_path || ""}
                          onChange={handleInputChange}
                          onBlur={handleBlur}
                          placeholder="Enter URL"
                          className={`form-control ${touched.media_path && errors.media_path ? "is-invalid" : ""
                            }`}
                        />
                        {touched.media_path && errors.media_path && (
                          <div className="invalid-feedback">{errors.media_path}</div>
                        )}
                      </div>
                    ) : (
                      <div>
                        <input
                          type="file"
                          name="mediaFileInput"
                          id="media-upload"
                          className={`form-control ${touched.media_path && errors.media_path ? "is-invalid" : ""
                            }`}
                          onChange={handleFileChange}
                          accept="image/*"
                        />
                        {touched.media_path && errors.media_path && (
                          <div className="invalid-feedback">{errors.media_path}</div>
                        )}

                        {selectedMediaFile && (
                          <div className="form-text text-muted">
                            Selected: {selectedMediaFile.name}
                          </div>
                        )}
                      </div>
                    )}
                    {mediaPreviewUrl && (
                      <div className="mt-2">
                        <h6>Preview:</h6>
                        {currentItem.type == 1 && (
                          <img
                            src={mediaPreviewUrl}
                            alt="Media Preview"
                            className="img-thumbnail"
                            style={{
                              width: "100%",
                              maxWidth: "200px",
                              height: "auto",
                              objectFit: "cover",
                            }}
                          />
                        )}
                      </div>
                    )}
                  </div>

                  <div className="col-md-6">
                    <label htmlFor="thumbnail-upload" className="form-label">
                      Thumbnail File{" "} ({REQUIRED_DIMENSIONS.thumbnail.width} x {REQUIRED_DIMENSIONS.thumbnail.height} pixels, Max size: {MAX_FILE_SIZE / (1024 * 1024)} MB)
                      {!isEditing && (
                        <span className="text-danger ms-1">*</span>
                      )}
                    </label>
                    <div>
                      <input
                        type="file"
                        name="thumbnailFileInput"
                        id="thumbnail-upload"
                        className={`form-control ${touched.thumbnail && errors.thumbnail ? "is-invalid" : ""
                          }`}
                        onChange={handleFileChange}
                        accept="image/*"
                      />
                      {touched.thumbnail && errors.thumbnail && (
                        <div className="invalid-feedback">{errors.thumbnail}</div>
                      )}
                      {selectedThumbnailFile && (
                        <div className="form-text text-muted">
                          Selected: {selectedThumbnailFile.name}
                        </div>
                      )}
                    </div>
                    {thumbnailPreviewUrl && (
                      <div className="mt-2">
                        <h6>Thumbnail Preview:</h6>
                        <img
                          src={thumbnailPreviewUrl}
                          alt="Thumbnail Preview"
                          className="img-thumbnail"
                          style={{
                            width: "100%",
                            maxWidth: "150px",
                            height: "auto",
                            objectFit: "cover",
                          }}
                        />
                      </div>
                    )}
                  </div>
                  <div className="col-12 mt-4 text-end">
                    <button
                      type="button"
                      className="btn btn-sm btn-warning me-2"
                      onClick={handleSubmit}
                    >
                      {isEditing ? "Update Item" : "Add Item"}
                    </button>
                    <button
                      type="button"
                      className="btn btn-sm btn-secondary"
                      onClick={cancelForm}
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="row">
          <div className="col-md-12">
            <div className="card">
              <div className="card-header adminCardHeader d-flex justify-content-between align-items-center">
                <h4>Gallery Management</h4>
                <button onClick={() => openForm()} className="btn btn-sm btn-warning">
                  <i className="bi bi-plus-circle me-1"></i> Add New Item
                </button>
              </div>

              <div className="card-body">
                {/* Search bar */}
                <div className="row">
                  <div className="col-md-6">
                    <div className="d-flex justify-content-start align-items-center">
                      <label className="me-2">Search:</label>
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        value={searchTerm}
                        onChange={handleSearch}
                        style={{ width: '150px' }}
                      />
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="d-flex justify-content-end align-items-center">
                      <button className="btn btn-success mb-2" onClick={handleDownloadExcel}>
                        <i className="bi bi-file-earmark-excel-fill me-2"></i>
                        Excel
                      </button>
                    </div>
                  </div>
                </div>

                <div className="table-responsive">
                  <table className="table">
                    <thead className="table-light">
                      <tr>
                        <th>S.No.</th>
                        <th>Thumbnail</th>
                        <th>Title</th>
                        <th>Description</th>
                        <th className="text-center">Year</th>
                        <th className="text-center">Type</th>
                        <th className="text-center">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredData.map((item, i) => (
                        <tr key={item.id}>
                          <td>{(currentPage - 1) * itemsPerPage + i + 1}</td>
                          <td>
                            {item.thumbnail ? (
                              <img
                                src={imgURL + item.thumbnail}
                                alt={item.title}
                                className="img-thumbnail"
                                style={{
                                  width: "75px",
                                  height: "75px",
                                  objectFit: "cover",
                                }}
                              />
                            ) : (
                              <div
                                style={{
                                  width: "75px",
                                  height: "75px",
                                  border: "1px solid #ccc",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  fontSize: "12px",
                                  color: "#666",
                                  background: "#f8f9fa",
                                }}
                              >
                                No Thumb
                              </div>
                            )}
                          </td>
                          <td>{item.title}</td>
                          <td>
                            <div
                              className="text-truncate"
                              style={{ maxWidth: "200px" }}
                            >
                              {item.description}
                            </div>
                          </td>
                          <td className="text-center">{item.year}</td>
                          <td className="text-center">
                            <span
                              className={`badge ${item.type == 1
                                ? "bg-success"
                                : "bg-primary"
                                }`}
                            >
                              {item.type == 1 ? "Image" : item.type == 2 ? "Video" : ""}
                            </span>
                          </td>
                          <td className="text-center">
                            <div className="d-inline-flex gap-2">
                              <i
                                className="bi bi-pencil text-primary cursor-pointer"
                                onClick={() => openForm(item)}
                              ></i>
                              <button
                                className="btn btn-link text-danger p-0 border-0"
                                onClick={() => handleDelete(item.id)}
                                title="Delete"
                              >
                                <i className="bi bi-trash"></i>
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}

                      {filteredData.length === 0 && (
                        <tr>
                          <td colSpan="7" className="text-center text-muted">
                            No gallery items found. Add your first item!
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
                {/* Pagination Controls */}
                <PaginationControls
                  currentPage={currentPage}
                  totalPages={totalPages}
                  setCurrentPage={setCurrentPage}
                  itemsPerPage={itemsPerPage}
                  setItemsPerPage={setItemsPerPage}
                  totalItems={filtered?.length}
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GalleryManager;